/**
 * API functions for actions management
 */

import { apiGet, apiPost, apiPatch, apiPut, apiDelete } from '@/lib/api/axios';
import {
  Action,
  ActionCreate,
  ActionPriority,
  ActionStoredStatus,
  ActionUpdate,
  ActionListResponse,
  Group,
  GroupCreate,
  GroupUpdate,
  GroupListResponse,
  GroupMember,
  GroupMembersResponse,
  GroupMeetingAttendanceHeatmapResponse,
  GroupRoleType,
  GroupType,
  GroupResourcesResponse,
  JoinRequest,
  JoinRequestListResponse,
  EFMDomainCode,
  TagCatalogGroup
} from './types';
import type { PamQuestionRef } from 'src/features/pam/api/types';

export async function getTagCatalog(): Promise<TagCatalogGroup[]> {
  const response = await apiGet<{ groups: TagCatalogGroup[] }>('/tags', {
    meta: { globalError: false, context: 'tag-catalog' }
  });
  return response.data.groups;
}

/** Every action the current user can access, including actions they created, are assigned to, or have permission to view. */
export async function getActions(): Promise<ActionListResponse> {
  const response = await apiGet<ActionListResponse>('/actions/', {
    meta: { globalError: true, context: 'actions-list' }
  });
  return response.data;
}

/**
 * Get a specific action by ID
 */
export async function getAction(actionId: string): Promise<Action> {
  const response = await apiGet<Action>(`/actions/${actionId}`, {
    meta: { globalError: true, context: 'actions-get' }
  });
  return response.data;
}

/**
 * Create a new action
 */
export async function createAction(data: ActionCreate): Promise<Action> {
  const response = await apiPost<Action>('/actions/', data, {
    meta: { globalError: true, context: 'actions-create' }
  });
  return response.data;
}

/**
 * Update an existing action
 */
export async function updateAction(actionId: string, data: ActionUpdate): Promise<Action> {
  const response = await apiPatch<Action>(`/actions/${actionId}`, data, {
    meta: { globalError: true, context: 'actions-update' }
  });
  return response.data;
}

/**
 * Move an action from its current group into another group. The action's
 * reference ID is re-derived from the target group by the backend.
 */
export async function moveAction(actionId: string, targetGroupId: string): Promise<Action> {
  const response = await apiPost<Action>(
    `/actions/${actionId}/move`,
    { target_group_id: targetGroupId },
    { meta: { globalError: true, context: 'actions-move' } }
  );
  return response.data;
}

/**
 * Archive an action
 */
export async function archiveAction(actionId: string): Promise<Action> {
  const response = await apiPatch<Action>(
    `/actions/${actionId}/archive`,
    {},
    {
      meta: { globalError: true, context: 'actions-archive' }
    }
  );
  return response.data;
}

/**
 * Unarchive an action
 */
export async function unarchiveAction(actionId: string): Promise<Action> {
  const response = await apiPatch<Action>(
    `/actions/${actionId}/unarchive`,
    {},
    {
      meta: { globalError: true, context: 'actions-unarchive' }
    }
  );
  return response.data;
}

/**
 * Resolve an action by uploading evidence.
 */
export async function resolveAction(
  actionId: string,
  documentIds: string[],
  text?: string,
  meetingId?: string
): Promise<Action> {
  const response = await apiPost<Action>(
    `/actions/${actionId}/resolve`,
    { document_ids: documentIds, text: text ?? null, meeting_id: meetingId ?? null },
    { meta: { globalError: true, context: 'actions-resolve' } }
  );
  return response.data;
}

/**
 * Reopen a resolved action, setting its status back to open.
 */
export async function unresolveAction(actionId: string, documentIds: string[] = [], text?: string): Promise<Action> {
  const response = await apiPatch<Action>(
    `/actions/${actionId}/unresolve`,
    { document_ids: documentIds, text: text ?? null },
    {
      meta: { globalError: true, context: 'actions-unresolve' }
    }
  );
  return response.data;
}

/**
 * Add a text update with optional Document Hub evidence to an action.
 */
export async function addActionUpdate(
  actionId: string,
  text: string,
  documentIds: string[],
  meetingId?: string,
  mentions?: Array<{ user_id: number; name: string }>
): Promise<any> {
  const response = await apiPost<any>(
    `/actions/${actionId}/updates`,
    { text, document_ids: documentIds, meeting_id: meetingId ?? null, mentions: mentions ?? null },
    { meta: { globalError: true, context: 'actions-add-update' } }
  );
  return response.data;
}

/**
 * Get audit log entries for an action.
 */
export async function getActionAuditLogs(actionId: string): Promise<{
  audit_logs: Array<{
    id: string;
    action_id: string;
    user_id: number | null;
    old_value: Record<string, any> | null;
    new_value: Record<string, any> | null;
    text_update?: {
      id: string;
      user_id: number | null;
      text: string;
      created_at: string;
      files: Array<{
        id: string;
        document_id: number;
        external_id: string;
        title: string;
        created_at: string;
      }>;
    } | null;
    moved?: {
      from_group_name: string | null;
      to_group_name: string | null;
    } | null;
    is_create: boolean;
    created_at: string;
  }>;
}> {
  const response = await apiGet<any>(`/actions/${actionId}/audit-logs`, {
    meta: { globalError: true, context: 'actions-audit-logs' }
  });
  return response.data;
}

// Action Extraction API

export interface CreatedActionInfo {
  id: string;
  reference_id: string;
  description?: string | null;
  due_date: string | null;
  priority?: ActionPriority | null;
  assignees?: { user_id: number; name: string }[];
  assignee_emails?: string[] | null;
  status: ActionStoredStatus;
  is_archived: boolean;
}

export interface RelatedActionInfo {
  id: string;
  reference_id: string;
  description: string;
  due_date: string | null;
  status: ActionStoredStatus;
  assignees?: { user_id: number; name: string; email?: string | null }[];
}

export type ActionRelationshipType = 'new' | 'updates' | 'closes';

export interface SourceElementInfo {
  id: string;
  type: 'paragraph' | 'table' | 'figure';
  content: string;
  page_index: number;
  // Figure-specific fields
  figure_id?: number | null; // Numeric figure ID for loading SAS URL
  caption?: string | null; // Figure/table caption
}

export interface SpreadsheetSourceRef {
  /** Stable source ID containing the document, sheet, and A1 range. */
  id: string;
  sheet_name: string;
  cell_range: string;
}

export interface ExtractedActionItem {
  id?: string;
  description: string;
  priority?: ActionPriority | null;
  suggested_priority: string | null;
  suggested_timeframe: string | null;
  suggested_assignees: string[] | null;
  source_elements: SourceElementInfo[];
  spreadsheet_source_refs: SpreadsheetSourceRef[];
  created_action_id?: string;
  created_action?: CreatedActionInfo | null;
  is_ignored?: boolean;
  relationship_type?: ActionRelationshipType | null;
  related_action_id?: string | null;
  related_action?: RelatedActionInfo | null;
  suggested_note?: string | null;
  suggested_assignee_changes?: string[] | null;
  suggested_due_date_change?: string | null;
  // Source-agnostic reviewable state (any source). log_status null ⇒ open.
  log_status?: 'open' | 'completed' | null;
  due_date?: string | null;
  resolved_date?: string | null;
  assignee_emails?: string[] | null;
  progress_updates?: string[] | null;
  is_manual?: boolean;
  draft_id?: string | null;
  draft_state?: 'ready' | 'ignored' | 'applied' | 'failed' | null;
  draft_description?: string | null;
  draft_due_date?: string | null;
  draft_assignee_emails?: string[] | null;
  draft_relationship_type?: ActionRelationshipType | null;
  draft_related_action_id?: string | null;
}

// Extraction status types
export type ExtractionStatus =
  | 'pending'
  | 'processing'
  | 'ready'
  | 'failed'
  | 'archived'
  | 'enhancing_sources'
  | 'sources_ready'
  | 'sources_failed';

export interface ActionExtractionResponse {
  meeting_date: string | null;
  id: string;
  source_type: 'document_upload' | 'meeting_minutes' | 'action_log';
  /** Source document ULID (same id space as Document Hub `document_id`). */
  document_id: string | null;
  document_external_id: string | null;
  document_name: string | null;
  creator_id: number | null;
  status: ExtractionStatus;
  status_message: string | null;
  extracted_actions: ExtractedActionItem[] | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  needs_attention?: boolean | null;
}

function extractionSourceDocumentId(extraction: ActionExtractionResponse): string | null {
  // Prefer the resolved external id, but fall back to document_id — listing can
  // leave document_external_id null when the doc fetch is permission-denied.
  return extraction.document_external_id ?? extraction.document_id ?? null;
}

function extractionAllItemsSettled(extraction: ActionExtractionResponse): boolean {
  const items = extraction.extracted_actions ?? [];
  return items.length > 0 && items.every((item) => !!item.created_action_id || !!item.is_ignored);
}

/** Whether an extraction still needs user attention (in progress, failed, or unreviewed items). */
export function extractionNeedsAttention(extraction: ActionExtractionResponse): boolean {
  if (extraction.status === 'archived') return false;
  if (
    extraction.needs_attention != null &&
    (extraction.extracted_actions == null || extraction.extracted_actions.length === 0)
  ) {
    return extraction.needs_attention;
  }
  if (extraction.status === 'pending' || extraction.status === 'processing' || extraction.status === 'failed') {
    return true;
  }
  const readyLikeStatuses = new Set<ExtractionStatus>([
    'ready',
    'enhancing_sources',
    'sources_ready',
    'sources_failed'
  ]);
  if (!readyLikeStatuses.has(extraction.status)) return true;

  return !extractionAllItemsSettled(extraction);
}

export type DocumentActionReviewState = 'pending' | 'ready' | 'failed' | 'completed';

/**
 * Visual/review state for a document that has (or had) an action extraction.
 * Returns null for archived extractions (dismissed — nothing to surface on the doc).
 */
export function getExtractionReviewState(extraction: ActionExtractionResponse): DocumentActionReviewState | null {
  if (extraction.status === 'archived') return null;
  if (extraction.status === 'failed') return 'failed';
  if (
    extraction.status === 'pending' ||
    extraction.status === 'processing' ||
    extraction.status === 'enhancing_sources'
  ) {
    return 'pending';
  }
  if (extractionAllItemsSettled(extraction)) return 'completed';
  return 'ready';
}

export interface DocumentActionReviewInfo {
  extractionId: string;
  state: DocumentActionReviewState;
  groupId?: string;
}

const DOCUMENT_ACTION_REVIEW_PRIORITY = {
  failed: 0,
  ready: 1,
  pending: 2,
  completed: 3
} as const;

/** Build a document_id → action-import indicator lookup from a list of extractions. */
export function buildDocumentActionReviewLookup(
  extractions: ActionExtractionResponse[],
  groupId?: string
): (documentId: string) => DocumentActionReviewInfo | null {
  return (documentId: string) => {
    const candidates = extractions
      .map((extraction) => {
        const sourceDocumentId = extractionSourceDocumentId(extraction);
        if (sourceDocumentId !== documentId) return null;
        const state = getExtractionReviewState(extraction);
        if (!state) return null;
        return {
          extractionId: extraction.id,
          state,
          groupId,
          updatedAt: extraction.updated_at || extraction.created_at
        };
      })
      .filter((entry): entry is NonNullable<typeof entry> => !!entry)
      .sort((a, b) => {
        const priorityDiff = DOCUMENT_ACTION_REVIEW_PRIORITY[a.state] - DOCUMENT_ACTION_REVIEW_PRIORITY[b.state];
        if (priorityDiff !== 0) return priorityDiff;
        return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
      });

    const best = candidates[0];
    if (!best) return null;
    return {
      extractionId: best.extractionId,
      state: best.state,
      groupId: best.groupId
    };
  };
}

export interface ActionExtractionStatusResponse {
  id: string;
  status: ExtractionStatus;
  status_message: string | null;
  extracted_actions: ExtractedActionItem[] | null;
  error_message: string | null;
}

export interface CreateActionFromExtraction {
  extraction_item_id?: string;
  description: string;
  due_date?: string | null;
  priority?: 'low' | 'medium' | 'high' | null;
  assignee_emails?: string[] | null;
  relationship_type?: ActionRelationshipType;
  related_action_id?: string | null;
  // Historic import (relationship_type === 'new' only): create an already-
  // completed action with a historic resolution date + progress notes.
  initial_status?: 'resolved' | null;
  resolved_at?: string | null;
  progress_updates?: string[] | null;
}

export interface CreateActionsFromExtractionRequest {
  extraction_id: string;
  actions: CreateActionFromExtraction[];
  meeting_id?: string | null;
}

export interface SaveExtractionDraftRequest {
  draft_id?: string | null;
  extraction_id: string;
  meeting_id: string;
  extraction_item_id?: string | null;
  is_manual?: boolean;
  description: string;
  due_date?: string | null;
  assignee_emails?: string[] | null;
  relationship_type?: ActionRelationshipType;
  related_action_id?: string | null;
  state: 'ready' | 'ignored';
}

export interface ExtractionDraftResponse {
  id: string;
  extraction_id: string;
  meeting_id: string;
  extraction_item_id: string | null;
  is_manual: boolean;
  description: string;
  due_date: string | null;
  assignee_emails: string[] | null;
  relationship_type: ActionRelationshipType;
  related_action_id: string | null;
  state: 'ready' | 'ignored' | 'applied' | 'failed';
  created_action_id: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Upload a document and start action extraction
 */
export async function startExtractionFromDocument(
  documentId: string,
  groupId?: string | null,
  sourceType: 'document_upload' | 'action_log' = 'document_upload'
): Promise<ActionExtractionResponse> {
  const response = await apiPost<ActionExtractionResponse>(
    '/actions/extraction/from-document',
    { document_id: documentId, group_id: groupId ?? null, source_type: sourceType },
    { meta: { globalError: false, context: 'action-extraction-from-document' } }
  );
  return response.data;
}

/**
 * Get the status of an action extraction job
 */
export async function getExtractionStatus(extractionId: string): Promise<ActionExtractionStatusResponse> {
  const response = await apiGet<ActionExtractionStatusResponse>(`/actions/extraction/${extractionId}/status`, {
    meta: { globalError: false, context: 'action-extraction-status' }
  });
  return response.data;
}

/**
 * Get full details of an action extraction
 */
export async function getExtraction(
  extractionId: string,
  options?: { meetingId?: string | null; globalError?: boolean }
): Promise<ActionExtractionResponse> {
  const response = await apiGet<ActionExtractionResponse>(`/actions/extraction/${extractionId}`, {
    params: {
      meeting_id: options?.meetingId || undefined
    },
    meta: { globalError: options?.globalError ?? true, context: 'action-extraction-get' }
  });
  return response.data;
}

/**
 * List action extractions for the current user.
 * Pass `includeGroup` to also include extractions shared via groups (needed for
 * Document Hub badges on personal-library files used in group imports).
 */
export async function listExtractions(options?: {
  includeGroup?: boolean;
}): Promise<{ extractions: ActionExtractionResponse[] }> {
  const response = await apiGet<{ extractions: ActionExtractionResponse[] }>('/actions/extractions', {
    params: {
      include_group: options?.includeGroup ?? false
    },
    meta: { globalError: true, context: 'action-extractions-list' }
  });
  return response.data;
}

/**
 * List all action extractions for a specific group
 */
export async function getGroupExtractions(
  groupId: string,
  options?: { excludeMeetingLinked?: boolean; includeItems?: boolean }
): Promise<{ extractions: ActionExtractionResponse[] }> {
  const response = await apiGet<{ extractions: ActionExtractionResponse[] }>(`/actions/groups/${groupId}/extractions`, {
    params: {
      exclude_meeting_linked: options?.excludeMeetingLinked ?? false,
      include_items: options?.includeItems ?? true
    },
    meta: { globalError: true, context: 'group-extractions-list' }
  });
  return response.data;
}

/**
 * Create actions from reviewed extraction results
 */
export async function createActionsFromExtraction(
  request: CreateActionsFromExtractionRequest
): Promise<ActionListResponse> {
  const response = await apiPost<ActionListResponse>('/actions/extraction/create-actions', request, {
    meta: { globalError: true, context: 'action-extraction-create' }
  });
  return response.data;
}

export async function saveExtractionDraft(request: SaveExtractionDraftRequest): Promise<ExtractionDraftResponse> {
  const response = await apiPost<ExtractionDraftResponse>('/actions/extraction/save-draft', request, {
    meta: { globalError: true, context: 'action-extraction-save-draft' }
  });
  return response.data;
}

export async function applyMeetingActionDrafts(meetingId: string): Promise<{ applied_count: number }> {
  const response = await apiPost<{ applied_count: number }>(
    `/actions/meetings/${meetingId}/apply-drafts`,
    {},
    {
      meta: { globalError: true, context: 'meeting-action-drafts-apply' }
    }
  );
  return response.data;
}

/**
 * Archive an extraction job
 */
export async function archiveExtraction(extractionId: string): Promise<void> {
  await apiDelete(`/actions/extraction/${extractionId}`, {
    meta: { globalError: true, context: 'action-extraction-archive' }
  });
}

/**
 * Update the ignored status of an extraction item
 */
export async function updateExtractionItemIgnored(
  extractionId: string,
  itemId: string,
  isIgnored: boolean
): Promise<void> {
  await apiPatch(`/actions/extraction/${extractionId}/items/${itemId}/ignored`, {
    is_ignored: isIgnored
  });
}

export async function archiveAllExtractionItems(extractionId: string): Promise<void> {
  await apiPatch(`/actions/extraction/${extractionId}/items/archive`, {});
}

export async function createManualExtractionItem(
  extractionId: string,
  request: {
    description: string;
    source_element_ids: string[];
    spreadsheet_source_refs?: SpreadsheetSourceRef[];
    log_status: 'open' | 'completed';
    due_date: string | null;
    resolved_date: string | null;
    assignee_emails: string[] | null;
    progress_updates: string[] | null;
  }
): Promise<{ id: string }> {
  const response = await apiPost<{ id: string }>(`/actions/extraction/${extractionId}/items`, request, {
    meta: { globalError: false, context: 'action-extraction-item-create' }
  });
  return response.data;
}

/**
 * Partial update to an extraction item's reviewable state (resumable review).
 * Only the fields present are applied; omitted fields are left unchanged.
 * Replaces the retired action-log `updateActionLogItem`.
 */
export interface UpdateExtractionItemRequest {
  description?: string;
  log_status?: 'open' | 'completed' | null;
  due_date?: string | null;
  resolved_date?: string | null;
  assignee_emails?: string[] | null;
  progress_updates?: string[] | null;
  relationship_type?: ActionRelationshipType | null;
  related_action_id?: string | null;
  suggested_priority?: string | null;
}

export async function updateExtractionItem(
  extractionId: string,
  itemId: string,
  request: UpdateExtractionItemRequest
): Promise<void> {
  await apiPatch(`/actions/extraction/${extractionId}/items/${itemId}`, request, {
    meta: { globalError: false, context: 'action-extraction-item-update' }
  });
}

/**
 * Response for action extraction source
 */
export interface ActionExtractionSourceResponse {
  extraction_id: string;
  document_external_id: string;
  document_name: string;
  // Which import kind produced this action: 'document_upload' | 'meeting_minutes' | 'action_log'.
  source_type: string;
  source_elements: SourceElementInfo[];
  spreadsheet_source_refs: SpreadsheetSourceRef[];
  suggested_priority: string | null;
  suggested_timeframe: string | null;
}

/**
 * Extract actions from the canonical meeting minutes.
 *
 * Pass `externalWritingId` for tool-generated or imported minutes and
 * `externalDocumentId` for user-uploaded PDF minutes. Exactly one must be provided.
 */
export async function extractActionsFromMeetingMinutes(
  source: { externalWritingId: string } | { externalDocumentId: string },
  groupId?: string | null,
  meetingId?: string | null,
  forceRegenerate = false
): Promise<ActionExtractionResponse> {
  const sourceBody =
    'externalWritingId' in source
      ? { external_writing_id: source.externalWritingId }
      : { external_document_id: source.externalDocumentId };
  const response = await apiPost<ActionExtractionResponse>(
    '/actions/extraction/from-meeting-minutes',
    {
      ...sourceBody,
      group_id: groupId || null,
      meeting_id: meetingId || null,
      force_regenerate: forceRegenerate
    },
    {
      meta: { globalError: false, context: 'actions-extract-from-meeting-minutes' }
    }
  );
  return response.data;
}

/**
 * Get extraction source information for an action
 * Returns the document elements that this action was extracted from
 */
export async function getActionExtractionSource(actionId: string): Promise<ActionExtractionSourceResponse | null> {
  try {
    const response = await apiGet<ActionExtractionSourceResponse>(`/actions/${actionId}/extraction-source`, {
      meta: { globalError: false, context: 'action-extraction-source' }
    });
    return response.data;
  } catch (error: any) {
    // 404 means the action wasn't created from an extraction - not an error
    if (error?.response?.status === 404) {
      return null;
    }
    throw error;
  }
}

// =============================================================================
// Terms of Reference API
// =============================================================================

export type RecommendedMeetingFrequency =
  | 'weekly'
  | 'fortnightly'
  | 'monthly'
  | 'quarterly'
  | 'biannually'
  | 'annually'
  | 'ad_hoc'
  | 'unspecified';

export interface RecommendedRoleResponse {
  name: string;
  description: string;
}

export interface RecommendedMeetingScheduleResponse {
  cadence: string;
  recommended_frequency: RecommendedMeetingFrequency;
  notes: string;
}

export interface TermsOfReferenceExtractionResponse {
  external_document_id: string;
  document_name: string;
  summary: string;
  roles: RecommendedRoleResponse[];
  meeting_schedule: RecommendedMeetingScheduleResponse;
}

/**
 * Upload a Terms of Reference document for a group and return structured
 * hints (summary, recommended roles, meeting schedule).
 */
export async function extractTermsOfReference(
  groupId: string,
  file: File
): Promise<TermsOfReferenceExtractionResponse> {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiPost<TermsOfReferenceExtractionResponse>(
    `/team-groups/${groupId}/terms-of-reference/extract`,
    formData,
    { meta: { globalError: false, context: 'tor-extract' } }
  );
  return response.data;
}

// =============================================================================
// Groups API
// =============================================================================

/**
 * Get all groups for the user's tenant
 */
export interface DirectoryMemberPreview {
  user_id: number;
  firstname: string | null;
  surname: string | null;
  email: string;
}

export interface DirectoryActionHorizon {
  overdue: number;
  within_month: number;
  over_month: number;
  no_deadline: number;
}

export interface DirectoryStatutoryRow {
  recommendation_id: string;
  name: string;
  required: boolean;
  evidenced: boolean;
  dismissed: boolean;
  expiry_date: string | null;
  expiry_status: 'expired' | 'pending' | 'expiring' | 'valid';
}

/** Vacancies plus the group domain's appointments counted by Record Status. */
export interface DirectoryAppointmentHorizon {
  vacant: number;
  expired: number;
  pending: number;
  expiring: number;
  unevidenced: number;
  evidenced: number;
}

export interface DirectoryTrainingAlert {
  kind: 'appointment_expiring';
  title: string;
  subtitle_parts: string[];
  target_id: string;
}

export interface DirectoryUnfinalisedMeeting {
  id: string;
  title: string | null;
  scheduled_date: string;
}

export interface DirectoryPamCompliance {
  yes: number;
  no: number;
}

export interface DirectoryGroupCard {
  group_id: string;
  name: string;
  description: string | null;
  type: GroupType;
  visibility: 'private' | 'discoverable';
  domain: string | null;
  domain_name: string | null;
  user_permission: number | null;
  is_member: boolean;
  my_pending_request_id: string | null;
  member_count: number;
  admins: { user_id: number; email: string; firstname: string | null; surname: string | null }[];
  members: DirectoryMemberPreview[];
  next_meeting_date: string | null;
  unfinalised_meetings: DirectoryUnfinalisedMeeting[];
  open_action_horizon: DirectoryActionHorizon | null;
  your_action_horizon: DirectoryActionHorizon | null;
  statutory_documents: DirectoryStatutoryRow[];
  pending_join_requests: JoinRequest[];
  recent_activity: RecentActivityItem[];
  appointment_horizon: DirectoryAppointmentHorizon | null;
  training_alerts: DirectoryTrainingAlert[];
  pam_compliance: DirectoryPamCompliance | null;
  updated_at: string;
}

export interface DirectorySnapshotResponse {
  groups: DirectoryGroupCard[];
}

export async function getDirectorySnapshot(): Promise<DirectorySnapshotResponse> {
  const response = await apiGet<DirectorySnapshotResponse>('/team-groups/directory-snapshot', {
    meta: { globalError: false, context: 'groups-directory-snapshot' }
  });
  return response.data;
}

export async function getGroups(): Promise<GroupListResponse> {
  const response = await apiGet<GroupListResponse>('/team-groups/', {
    meta: { globalError: true, context: 'groups-list' }
  });
  return response.data;
}

/**
 * Get a specific group by ID
 */
export async function getGroup(groupId: string): Promise<Group> {
  const response = await apiGet<Group>(`/team-groups/${groupId}`, {
    meta: { globalError: false, context: 'groups-get' }
  });
  return response.data;
}

/**
 * Create a new group
 */
export async function createGroup(data: GroupCreate): Promise<Group> {
  const response = await apiPost<Group>('/team-groups/', data, {
    meta: { globalError: false, context: 'groups-create' }
  });
  return response.data;
}

/**
 * Update an existing group
 */
export async function updateGroup(groupId: string, data: GroupUpdate): Promise<Group> {
  const response = await apiPatch<Group>(`/team-groups/${groupId}`, data, {
    meta: { globalError: true, context: 'groups-update' }
  });
  return response.data;
}

/**
 * Update populate wizard progress for any team group (standard or safety).
 *
 * Fields use exclude_unset semantics on the backend, so:
 *   - omitting a field leaves it unchanged
 *   - passing `null` clears the field (where the column is nullable)
 */
export async function updatePopulateState(
  groupId: string,
  data: {
    populate_stage?: string;
    populate_completed_at?: string | null;
    add_completed_stage?: string;
  }
): Promise<Group> {
  const response = await apiPatch<Group>(`/team-groups/${groupId}/populate`, data, {
    meta: { globalError: true, context: 'groups-populate-update' }
  });
  return response.data;
}

/**
 * Update Terms of Reference for a safety group.
 */
export async function updateSafetyGroup(
  groupId: string,
  data: {
    terms_of_reference_id?: string | null;
    domain?: EFMDomainCode | null;
  }
): Promise<Group> {
  const response = await apiPatch<Group>(`/team-groups/${groupId}/safety`, data, {
    meta: { globalError: true, context: 'groups-safety-update' }
  });
  return response.data;
}

/**
 * Archive a group (soft delete)
 * This is irreversible from the UI.
 */
export async function archiveGroup(groupId: string): Promise<void> {
  await apiDelete(`/team-groups/${groupId}`, {
    meta: { globalError: true, context: 'groups-archive' }
  });
}

// =============================================================================
// Group Members API
// =============================================================================

export async function getGroupMembers(groupId: string): Promise<GroupMembersResponse> {
  const response = await apiGet<GroupMembersResponse>(`/team-groups/${groupId}/members`, {
    meta: { globalError: false, context: 'group-members-list' }
  });
  return response.data;
}

export async function getGroupMeetingAttendanceHeatmap(
  groupId: string
): Promise<GroupMeetingAttendanceHeatmapResponse> {
  const response = await apiGet<GroupMeetingAttendanceHeatmapResponse>(
    `/team-groups/${groupId}/meeting-attendance-heatmap`,
    { meta: { globalError: false, context: 'group-meeting-attendance-heatmap' } }
  );
  return response.data;
}

export async function addGroupMember(
  groupId: string,
  email: string,
  groupRoleType: GroupRoleType,
  message?: string
): Promise<GroupMember> {
  const response = await apiPost<GroupMember>(
    `/team-groups/${groupId}/members`,
    { email, group_role_type: groupRoleType, ...(message ? { message } : {}) },
    { meta: { globalError: false, context: 'group-members-add' } }
  );
  return response.data;
}

export async function changeGroupMemberRole(
  groupId: string,
  userId: number,
  groupRoleType: GroupRoleType
): Promise<GroupMember> {
  const response = await apiPatch<GroupMember>(
    `/team-groups/${groupId}/members/${userId}`,
    { group_role_type: groupRoleType },
    { meta: { globalError: false, context: 'group-members-change-role' } }
  );
  return response.data;
}

export async function removeGroupMember(groupId: string, userId: number): Promise<void> {
  await apiDelete(`/team-groups/${groupId}/members/${userId}`, {
    meta: { globalError: false, context: 'group-members-remove' }
  });
}

// =============================================================================
// Group Resources API
// =============================================================================

export async function getGroupResources(groupId: string): Promise<GroupResourcesResponse> {
  const response = await apiGet<GroupResourcesResponse>(`/team-groups/${groupId}/resources`, {
    meta: { globalError: false, context: 'group-resources-list' }
  });
  return response.data;
}

// =============================================================================
// Recent Activity API
// =============================================================================

export type ActivityItemKind =
  | 'action_created'
  | 'action_state_changed'
  | 'action_progress_update'
  | 'extraction_meeting'
  | 'extraction_report'
  | 'resource_shared'
  | 'meeting_approval'
  | 'member_added'
  | 'group_created'
  | 'meeting_added'
  | 'meeting_series_added'
  | 'meeting_resource_added'
  | 'meeting_finalised'
  | 'appointment_created'
  | 'appointment_revoked'
  | 'certificate_added'
  | 'dashboard_data_entered';

export interface ActivityItemFacts {
  actor: string | null;
  subject: string;
  metadata?: Record<string, unknown>;
}

export interface RecentActivityItem {
  kind: ActivityItemKind;
  facts: ActivityItemFacts;
  timestamp: string | null;
  relevant_to_user: boolean;
  target_ids: string[];
}

export interface RecentActivityResponse {
  items: RecentActivityItem[];
}

export async function getGroupRecentActivity(
  groupId: string,
  relevantToMe: boolean = false
): Promise<RecentActivityResponse> {
  const response = await apiGet<RecentActivityResponse>(`/team-groups/${groupId}/recent-activity`, {
    params: { relevant_to_user: relevantToMe },
    meta: { globalError: false, context: 'group-recent-activity' }
  });
  return response.data;
}

export interface GroupOverviewFollowUp {
  label: string;
  prompt: string;
}

export interface GroupOverviewResourceLink {
  id: string;
  resource_id: string;
  resource_type: string;
  title: string;
}

export interface GroupOverviewResponse {
  body: string | null;
  generated_at: string | null;
  prompt_version: string | null;
  week_start: string;
  is_fresh: boolean;
  status: 'ready' | 'stale' | 'missing';
  follow_ups: GroupOverviewFollowUp[];
  resource_links?: GroupOverviewResourceLink[];
}

export async function getGroupOverview(groupId: string): Promise<GroupOverviewResponse> {
  const response = await apiGet<GroupOverviewResponse>(`/team-groups/${groupId}/overview`, {
    meta: { globalError: false, context: 'group-overview' }
  });
  return response.data;
}

export async function refreshGroupOverview(groupId: string): Promise<GroupOverviewResponse> {
  const response = await apiPost<GroupOverviewResponse>(`/team-groups/${groupId}/overview/refresh`, undefined, {
    meta: { globalError: false, context: 'group-overview-refresh' }
  });
  return response.data;
}

export async function getDirectoryOverview(): Promise<GroupOverviewResponse> {
  const response = await apiGet<GroupOverviewResponse>('/team-groups/overview', {
    meta: { globalError: false, context: 'directory-overview' }
  });
  return response.data;
}

export async function refreshDirectoryOverview(): Promise<GroupOverviewResponse> {
  const response = await apiPost<GroupOverviewResponse>('/team-groups/overview/refresh', undefined, {
    meta: { globalError: false, context: 'directory-overview-refresh' }
  });
  return response.data;
}

const RESOURCE_UNSHARE_ROUTES: Record<string, (id: string, groupId: string) => string> = {
  document: (id, gid) => `docs/document/${id}/group-shares/${gid}`,
  chat: (id, gid) => `chat/chats/${id}/group-shares/${gid}`,
  review: (id, gid) => `review/reviews/${id}/group-shares/${gid}`,
  writing: (id, gid) => `writing/${id}/group-shares/${gid}`,
  dashboard: (id, gid) => `dashboards/nodes/${id}/group-shares/${gid}`,
  specialist: (id, gid) => `specialists/${id}/group-shares/${gid}`
};

export async function removeResourceGroupShare(
  resourceType: string,
  resourceExternalId: string,
  groupId: string
): Promise<void> {
  const routeFn = RESOURCE_UNSHARE_ROUTES[resourceType];
  if (!routeFn) throw new Error(`Unsupported resource type: ${resourceType}`);
  await apiDelete(routeFn(resourceExternalId, groupId), {
    meta: { globalError: false, context: 'remove-resource-group-share' }
  });
}

/**
 * Get all actions for a specific group
 */
export async function getGroupActions(
  groupId: string,
  options?: { openOnly?: boolean; assignedToMe?: boolean; limit?: number }
): Promise<ActionListResponse> {
  const response = await apiGet<ActionListResponse>(`/actions/groups/${groupId}/actions`, {
    params: {
      open_only: options?.openOnly ?? false,
      assigned_to_me: options?.assignedToMe ?? false,
      ...(options?.limit != null ? { limit: options.limit } : {})
    },
    meta: { globalError: true, context: 'group-actions-list' }
  });
  return response.data;
}

// =============================================================================
// Associated Users API
// =============================================================================

/**
 * User info returned by associated users endpoints
 */
export interface AssociatedUser {
  user_id: number;
  email: string;
  firstname?: string | null;
  surname?: string | null;
}

/**
 * Response from associated users endpoints
 */
export interface AssociatedUsersResponse {
  users: AssociatedUser[];
}

/**
 * Get all users associated with a group
 * Includes users from group permissions, action creators/assignees,
 * action log entries, and action updates
 */
export async function getGroupAssociatedUsers(groupId: string): Promise<AssociatedUser[]> {
  const response = await apiGet<AssociatedUsersResponse>(`/actions/groups/${groupId}/associated-users`, {
    meta: { globalError: false, context: 'group-associated-users' }
  });
  return response.data.users;
}

/**
 * Get all users associated with an action
 * Includes action creator/assignee, action permissions,
 * action log entries, and action updates
 */
export async function getActionAssociatedUsers(actionId: string): Promise<AssociatedUser[]> {
  const response = await apiGet<AssociatedUsersResponse>(`/actions/${actionId}/associated-users`, {
    meta: { globalError: false, context: 'action-associated-users' }
  });
  return response.data.users;
}

// =============================================================================
// Group Reminder API
// =============================================================================

/**
 * Response for group reminder mute status
 */
export interface GroupReminderMuteStatus {
  group_id: string;
  is_muted: boolean;
}

/**
 * Response for verifying group reminder mute token
 */
export interface VerifyGroupReminderMuteTokenResponse {
  valid: boolean;
  email: string | null;
  user_id: number | null;
  group_id: string | null;
  group_name: string | null;
  already_muted: boolean;
  message: string | null;
}

/**
 * Get group reminder mute status for the current user (authenticated)
 */
export async function getGroupReminderMuteStatus(groupId: string): Promise<GroupReminderMuteStatus> {
  const response = await apiGet<GroupReminderMuteStatus>(`/actions/groups/${groupId}/reminder/mute-status`, {
    meta: { globalError: false, context: 'group-reminder-mute-status' }
  });
  return response.data;
}

/**
 * Mute group reminders (authenticated)
 */
export async function muteGroupReminder(groupId: string): Promise<GroupReminderMuteStatus> {
  const response = await apiPost<GroupReminderMuteStatus>(
    `/actions/groups/${groupId}/reminder/mute`,
    {},
    {
      meta: { globalError: false, context: 'group-reminder-mute' }
    }
  );
  return response.data;
}

/**
 * Unmute group reminders (authenticated)
 */
export async function unmuteGroupReminder(groupId: string): Promise<GroupReminderMuteStatus> {
  const response = await apiPost<GroupReminderMuteStatus>(
    `/actions/groups/${groupId}/reminder/unmute`,
    {},
    {
      meta: { globalError: false, context: 'group-reminder-unmute' }
    }
  );
  return response.data;
}

/**
 * Verify a group reminder mute token from an unsubscribe email link
 */
export async function verifyGroupReminderMuteToken(token: string): Promise<VerifyGroupReminderMuteTokenResponse> {
  const response = await apiGet<VerifyGroupReminderMuteTokenResponse>(
    `/actions/groups/reminder/mute/verify?token=${encodeURIComponent(token)}`,
    {
      meta: { globalError: false, context: 'group-reminder-mute-verify' }
    }
  );
  return response.data;
}

/**
 * Mute group reminders using a JWT token (from email link)
 */
export async function muteGroupReminderWithToken(token: string): Promise<GroupReminderMuteStatus> {
  const response = await apiPost<GroupReminderMuteStatus>(
    `/actions/groups/reminder/mute?token=${encodeURIComponent(token)}`,
    {},
    {
      meta: { globalError: false, context: 'group-reminder-mute-token' }
    }
  );
  return response.data;
}

/**
 * Unmute group reminders using a JWT token (from email link)
 */
export async function unmuteGroupReminderWithToken(token: string): Promise<GroupReminderMuteStatus> {
  const response = await apiPost<GroupReminderMuteStatus>(
    `/actions/groups/reminder/unmute?token=${encodeURIComponent(token)}`,
    {},
    {
      meta: { globalError: false, context: 'group-reminder-unmute-token' }
    }
  );
  return response.data;
}

// =============================================================================
// Group join requests
// =============================================================================

/**
 * Tenant admin/champion adds themselves to a safety group they are not in.
 */
export async function joinSafetyGroup(groupId: string, groupRoleType: GroupRoleType = 'viewer'): Promise<GroupMember> {
  const response = await apiPost<GroupMember>(
    `/team-groups/${groupId}/self-join`,
    { group_role_type: groupRoleType },
    { meta: { globalError: false, context: 'group-self-join' } }
  );
  return response.data;
}

/**
 * Submit a request to join a group.
 */
export async function requestToJoinGroup(groupId: string, message?: string | null): Promise<JoinRequest> {
  const response = await apiPost<JoinRequest>(
    `/team-groups/${groupId}/join-requests`,
    { message: message ?? null },
    { meta: { globalError: false, context: 'group-join-request-create' } }
  );
  return response.data;
}

/**
 * List pending join requests for a group. Only group Admins may call.
 */
export async function listGroupJoinRequests(groupId: string): Promise<JoinRequestListResponse> {
  const response = await apiGet<JoinRequestListResponse>(`/team-groups/${groupId}/join-requests`, {
    meta: { globalError: false, context: 'group-join-requests-list' }
  });
  return response.data;
}

/**
 * Approve a pending join request and add the requester to the group.
 */
export async function approveJoinRequest(
  groupId: string,
  requestId: string,
  options?: { groupRoleType?: GroupRoleType }
): Promise<JoinRequest> {
  const response = await apiPost<JoinRequest>(
    `/team-groups/${groupId}/join-requests/${requestId}/approve`,
    {
      group_role_type: options?.groupRoleType ?? 'viewer'
    },
    { meta: { globalError: false, context: 'group-join-request-approve' } }
  );
  return response.data;
}

/**
 * Reject a pending join request.
 */
export async function rejectJoinRequest(groupId: string, requestId: string): Promise<JoinRequest> {
  const response = await apiPost<JoinRequest>(
    `/team-groups/${groupId}/join-requests/${requestId}/reject`,
    {},
    { meta: { globalError: false, context: 'group-join-request-reject' } }
  );
  return response.data;
}

// =============================================================================
// Domain catalogue
// =============================================================================

/**
 * List the platform-wide safety / Estates domain catalogue.
 *
 * Used by the create-safety-group modal to resolve a domain code
 * (e.g. ``fire``) to its UUID before POSTing the new group. The
 * catalogue is stable across tenants, so callers can cache freely.
 */
export async function getCatalogueDomains(): Promise<import('./types').CatalogueDomain[]> {
  const response = await apiGet<import('./types').CatalogueDomain[]>('/catalogue/domains', {
    meta: { globalError: false, context: 'catalogue-domains' }
  });
  return response.data;
}

/**
 * List the domain UUIDs the caller's tenant must maintain a safety group for.
 *
 * Driven by ``environment_safety_group_requirements`` joined to the
 * tenant's regulatory environment. The frontend uses this to render
 * the "Required" pill, drive the empty-state setup CTAs, and compute
 * the coverage pill against the required denominator only.
 */
export async function getSafetyGroupRequirements(): Promise<import('./types').SafetyGroupRequirements> {
  const response = await apiGet<import('./types').SafetyGroupRequirements>('/catalogue/safety-group-requirements', {
    meta: { globalError: false, context: 'catalogue-safety-group-requirements' }
  });
  return response.data;
}

export interface StatutoryDocumentRow {
  recommendation_id: string;
  name: string;
  description: string;
  renewal_frequency_months: number | null;
  required: boolean;
  pam_questions: PamQuestionRef[];
  source_element_ids: string[];
  sources: {
    document_id: string;
    document_name: string;
    source_element_id: string;
    source_type: 'paragraph' | 'table' | 'figure' | string;
    content: string;
    page_index: number;
    caption: string | null;
    figure_id: number | null;
  }[];
  linked_documents: { id: string; name: string; expiry_date: string | null }[];
  other_evidence: {
    id: string;
    text: string | null;
    contact_name: string | null;
    contact_details: string | null;
    external_link: string | null;
  }[];
  training_evidence: {
    id: string;
    kind: 'appointment';
    label: string;
    expiry_date: string | null;
    starts_on: string | null;
  }[];
  sourced_from_training: boolean;
  expiry_date: string | null;
  expiry_status: 'expired' | 'pending' | 'expiring' | 'valid';
  dismissal: {
    id: string;
    reason: string | null;
    created_at: string;
    created_by_name: string | null;
  } | null;
}

export interface GroupStatutoryDocumentsResponse {
  domain: EFMDomainCode | null;
  compliance_percent: number;
  documents: StatutoryDocumentRow[];
}

export async function getGroupStatutoryDocuments(groupId: string): Promise<GroupStatutoryDocumentsResponse> {
  const response = await apiGet<GroupStatutoryDocumentsResponse>(`/team-groups/${groupId}/statutory-documents`, {
    meta: { globalError: false, context: 'group-statutory-documents' }
  });
  return response.data;
}

export async function linkGroupStatutoryDocument(
  groupId: string,
  recommendationId: string,
  body: { document_ids: string[] }
): Promise<void> {
  await apiPut(`/team-groups/${groupId}/statutory-documents/${recommendationId}`, body, {
    meta: { globalError: true, context: 'group-statutory-document-link' }
  });
}

export async function unlinkGroupStatutoryDocument(
  groupId: string,
  recommendationId: string,
  documentId: string
): Promise<void> {
  await apiDelete(`/team-groups/${groupId}/statutory-documents/${recommendationId}/${documentId}`, {
    meta: { globalError: true, context: 'group-statutory-document-unlink' }
  });
}

export type StatutoryOtherEvidencePayload = {
  text: string | null;
  contact_name: string | null;
  contact_details: string | null;
  external_link: string | null;
};

export async function importGroupStatutoryEvidence(
  groupId: string,
  body: {
    links: { recommendation_id: string; document_ids: string[] }[];
    notes: (StatutoryOtherEvidencePayload & { recommendation_id: string })[];
  }
): Promise<void> {
  await apiPost(`/team-groups/${groupId}/statutory-documents/import`, body, {
    meta: { globalError: true, context: 'group-statutory-document-import' }
  });
}

export async function addGroupStatutoryNote(
  groupId: string,
  recommendationId: string,
  body: StatutoryOtherEvidencePayload
): Promise<void> {
  await apiPost(`/team-groups/${groupId}/statutory-documents/${recommendationId}/notes`, body, {
    meta: { globalError: true, context: 'group-statutory-document-note' }
  });
}

export async function updateGroupStatutoryNote(
  groupId: string,
  recommendationId: string,
  noteId: string,
  body: StatutoryOtherEvidencePayload
): Promise<void> {
  await apiPut(`/team-groups/${groupId}/statutory-documents/${recommendationId}/notes/${noteId}`, body, {
    meta: { globalError: true, context: 'group-statutory-document-note-update' }
  });
}

export async function deleteGroupStatutoryNote(
  groupId: string,
  recommendationId: string,
  noteId: string
): Promise<void> {
  await apiDelete(`/team-groups/${groupId}/statutory-documents/${recommendationId}/notes/${noteId}`, {
    meta: { globalError: true, context: 'group-statutory-document-note-delete' }
  });
}

export async function dismissGroupStatutoryDocument(
  groupId: string,
  recommendationId: string,
  reason: string | null
): Promise<void> {
  await apiPost(
    `/team-groups/${groupId}/statutory-documents/${recommendationId}/dismissal`,
    { reason },
    { meta: { globalError: true, context: 'group-statutory-document-dismiss' } }
  );
}

export async function restoreGroupStatutoryDocument(groupId: string, recommendationId: string): Promise<void> {
  await apiDelete(`/team-groups/${groupId}/statutory-documents/${recommendationId}/dismissal`, {
    meta: { globalError: true, context: 'group-statutory-document-restore' }
  });
}
